import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import {
  getBorrowerCount,
  getDashboardData,
} from "../../../service/cat15.service";
import { DashboardData } from "../../../models/dto/cat15/cat15.types";
import PortfolioStatCard from "../../../components/cat15/PortfolioStatCard";
import {
  FeBarChartRoundedIcon,
  FeBusinessCenterIcon,
  FeCalendarIcon,
  FeGroupIcon,
  FeReportIcon,
} from "../../../components/cat15/FeIcons";
import { formatNumber, formatTHB } from "../format";
import OverviewSelect from "./OverviewSelect";

/** Same default as the dashboard's filter (dashboard/index.tsx defaultFilter). */
const DEFAULT_REPORTING_YEAR = 2025;
/** The dashboard's year picker starts at 2020. */
const FIRST_REPORTING_YEAR = 2020;

/**
 * Top of the Borrowers overview tab (Figma 7063:12865): the reporting-year
 * heading and four portfolio stat cards.
 */
const PortfolioOverview = () => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [year, setYear] = useState(DEFAULT_REPORTING_YEAR);
  const [data, setData] = useState<DashboardData | undefined>();
  const [borrowerCount, setBorrowerCount] = useState<number | undefined>();

  useEffect(() => {
    let active = true;
    getDashboardData({ reportingYear: year })
      .then((d) => active && setData(d))
      // handleApiError already shows the alert; keep the rejection handled.
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [year]);

  useEffect(() => {
    getBorrowerCount()
      .then(setBorrowerCount)
      .catch(() => undefined);
  }, []);

  const yearOptions = useMemo(() => {
    const last = Math.max(new Date().getFullYear(), DEFAULT_REPORTING_YEAR);
    return Array.from({ length: last - FIRST_REPORTING_YEAR + 1 }, (_, i) => {
      const y = String(last - i);
      return { value: y, label: y };
    });
  }, []);

  const placeholder = "—";
  const kpis = data?.kpis;

  return (
    <>
      {/* Heading + Reporting Year (Figma 7074:32782) */}
      <Box display="flex" alignItems="center" gap="40px">
        <Typography
          flex={1}
          minWidth={0}
          fontSize="18px"
          fontWeight={600}
          lineHeight="24px"
          color="var(--dark)"
        >
          {t("Portfolio-Overview-Title", { year })}
        </Typography>
        <Box display="flex" alignItems="center" gap="20px" flexShrink={0}>
          <Typography
            fontSize="14px"
            fontWeight={500}
            lineHeight="24px"
            color="var(--dark)"
          >
            {t("Filter-Reporting-Year")}
          </Typography>
          <OverviewSelect
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            options={yearOptions}
            startIcon={<FeCalendarIcon />}
            textPr={8}
            ariaLabel={t("Filter-Reporting-Year")}
          />
        </Box>
      </Box>

      {/* Stat cards (Figma 7063:13617) */}
      <Box display="flex" gap="16px" alignItems="flex-start">
        <PortfolioStatCard
          label={t("KPI-Total-Borrowers")}
          value={
            borrowerCount === undefined
              ? placeholder
              : formatNumber(borrowerCount)
          }
          icon={<FeGroupIcon />}
          iconColor="var(--green)"
          iconBg="var(--tint-green)"
        />
        <PortfolioStatCard
          label={t("KPI-Total-Outstanding-Balance")}
          value={kpis ? formatTHB(kpis.outstanding.value) : placeholder}
          icon={<FeBusinessCenterIcon />}
          iconColor="var(--chart-table-surface-general-scope-1)"
          iconBg="var(--tint-royal-blue)"
        />
        <PortfolioStatCard
          label={t("Total-Financed-Emissions")}
          value={
            kpis
              ? `${formatNumber(kpis.financedEmissions.value)} tCO₂e`
              : placeholder
          }
          icon={<FeReportIcon />}
          iconColor="var(--status-pending-text)"
          iconBg="var(--tint-amber)"
        />
        <PortfolioStatCard
          label={t("KPI-Average-DQS")}
          value={kpis ? formatNumber(kpis.weightedDqs.value) : placeholder}
          icon={<FeBarChartRoundedIcon />}
          iconColor="var(--pink)"
          iconBg="var(--tint-pink)"
        />
      </Box>
    </>
  );
};

export default PortfolioOverview;
